import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "pc-qui-chauffe",
  silo: "performance",
  h1: "PC qui chauffe et ventilateur bruyant : que faire avant la panne ?",
  metaTitle: "PC qui chauffe : ventilateur bruyant, quelles solutions ?",
  metaDescription: "Un ordinateur qui surchauffe peut tomber en panne. Apprenez à identifier les causes et à protéger votre PC avant qu'il soit trop tard.",
  excerpt: "Un ventilateur qui s'emballe, un PC brûlant au toucher, des coupures soudaines... La surchauffe est l'une des principales causes de pannes matérielles. Voici comment l'identifier et y remédier rapidement.",
  publishedAt: "2024-03-08",
  readingMinutes: 5,
  relatedSlugs: ["pc-lent-windows-10-11", "demarrage-lent-windows", "disque-dur-sature"],
  faq: [
    {
      question: "Quelle est la température normale d'un processeur ?",
      answer: "Au repos, entre 30°C et 50°C est normal. En charge (jeu, vidéo), jusqu'à 80°C est acceptable. Au-delà de 90°C, c'est une zone de danger et le PC risque de s'éteindre ou de se dégrader.",
    },
    {
      question: "Mon PC s'éteint tout seul -- est-ce la surchauffe ?",
      answer: "Très probablement. C'est le mécanisme de protection automatique de votre processeur. S'il atteint une température critique, il coupe l'alimentation pour éviter d'être endommagé.",
    },
    {
      question: "Puis-je nettoyer moi-même l'intérieur de mon PC ?",
      answer: "Oui, avec une bombe à air comprimé vendue en magasin informatique. Éteignez le PC, débranchez-le, et soufflez dans les grilles de ventilation. Évitez d'utiliser un aspirateur classique qui génère de l'électricité statique.",
    },
  ],
  content: [
    {
      type: "p",
      text: "La surchauffe est souvent silencieuse au début, puis elle devient évidente : le ventilateur tourne à plein régime en permanence, le PC se met à ramer, ou il s'éteint brutalement. Si vous ne traitez pas ce problème, vous risquez d'endommager définitivement des composants coûteux.",
    },
    {
      type: "h2",
      text: "Pourquoi mon PC chauffe-t-il trop ?",
    },
    {
      type: "ul",
      items: [
        "Accumulation de poussière dans les grilles de ventilation et sur le ventilateur",
        "Pâte thermique séchée entre le processeur et son refroidisseur (après 3 à 5 ans)",
        "Ventilateur défaillant ou obstrué",
        "Mauvaise aération du PC (posé sur un tapis, contre un mur, dans un meuble fermé)",
        "Logiciel gourmand ou virus qui sollicite 100% du processeur en permanence",
        "PC portable avec grilles de ventilation bouchées par la poussière",
      ],
    },
    {
      type: "h2",
      text: "Comment surveiller la température de son PC",
    },
    {
      type: "p",
      text: "Le logiciel gratuit HWMonitor (disponible sur hwinfo.com) vous permet de voir en temps réel la température de votre processeur, de la carte graphique et du disque dur. C'est l'outil de référence pour diagnostiquer une surchauffe.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "Signes d'alerte à ne pas ignorer",
      text: "PC qui s'éteint tout seul, écran qui se fige sans raison, performances qui chutent après quelques minutes d'utilisation : ce sont les symptômes classiques d'une surchauffe.",
    },
    {
      type: "h2",
      text: "Solutions pour refroidir votre PC",
    },
    {
      type: "ol",
      items: [
        "Nettoyage à l'air comprimé des grilles de ventilation (à faire tous les 12 à 18 mois)",
        "Vérifier que le PC est bien ventilé : posé sur une surface dure, à au moins 10 cm des murs",
        "Pour un PC portable : utilisez un support surélevé ou un refroidisseur externe",
        "Sur un PC fixe : vérifier que tous les ventilateurs tournent correctement",
        "Renouveler la pâte thermique si le PC a plus de 4 ans (intervention matérielle)",
        "Vérifier avec le Gestionnaire des tâches qu'aucun programme n'utilise 100% du processeur",
      ],
    },
    {
      type: "h2",
      text: "Le lien entre surchauffe et lenteur",
    },
    {
      type: "p",
      text: "Quand le processeur chauffe trop, Windows réduit automatiquement ses performances pour éviter la casse -- c'est le 'throttling thermique'. Résultat : votre [PC devient lent](slug:pc-lent-windows-10-11) sans raison apparente, surtout sur des tâches simples. Résoudre la surchauffe résout souvent aussi les problèmes de lenteur.",
    },
    {
      type: "h2",
      text: "Quand faut-il appeler un professionnel ?",
    },
    {
      type: "p",
      text: "Si le nettoyage ne suffit pas, que votre PC continue de surchauffer ou de s'éteindre, un technicien peut se déplacer pour analyser les températures et identifier la cause précise, puis réaliser sur place les interventions physiques (remplacement du ventilateur, pâte thermique).",
    },
  ],
};

export default article;
